import React, { useState, useEffect, useCallback } from 'react';
import apiClient, { openPaymentDocumentFile, openExpenseDocumentFile, openTransferDocumentFile } from '../api/client';
import { localDateKey } from '../utils/formatters';

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0,00';
  return new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const MONTHS = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

const formatDate = (d) => {
  if (!d) return '';
  const clean = d.replace(/\//g, '-');
  const parts = clean.split('-');
  if (parts.length === 3) {
    const [y, m, day] = parts;
    return `${day} ${MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
  }
  return d;
};

const formatUploadDate = (iso) => {
  if (!iso) return 'Sin fecha';
  return formatDate(localDateKey(iso));
};

const toSortable = (d) => (d || '').replace(/\//g, '-');

const CATEGORY_LABELS = {
  comision_pago_movil: 'Comisión Pago Móvil',
  comision_bancaria: 'Comisión Bancaria',
  comision_transferencia: 'Comisión Transferencia',
  comision_pago_proveedores: 'Comisión Proveedores',
  comision_mantenimiento: 'Mant. POS',
  domiciliacion: 'Domiciliación POS',
  pago_proveedores: 'Pago Proveedores',
  pago_movil: 'Pago Móvil',
  deposito: 'Depósito',
  transferencia: 'Transferencia',
  transferencia_interna: 'Transf. Interna',
  nota_debito: 'Nota de Débito',
  prestamo_interno: 'Préstamo Interno',
  biopago: 'Biopago',
  comision_biopago: 'Comisión Biopago',
  igtf: 'IGTF',
  pos_debito: 'POS Débito',
  pos_credito: 'POS Crédito',
  sin_clasificar: 'Sin clasificar',
  otro: 'Otro',
};

const isAutoMatch = (mov) =>
  mov.amount > 0 ||
  (mov.category && (mov.category.startsWith('comision') || mov.category === 'igtf' || mov.category === 'domiciliacion'));

const ManualReconciliation = () => {
  const [uploads, setUploads] = useState([]);
  const [stores, setStores] = useState({});   // { store_id: store_name }
  const [selectedUpload, setSelectedUpload] = useState('');
  const [movements, setMovements] = useState([]);
  // manualStatus: { [movement_id]: { status: 'matched'|'pending', notes, updated_by } }
  const [manualStatus, setManualStatus] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchMov, setSearchMov] = useState(null);       // movimiento para el que se busca recibo
  const [candidates, setCandidates] = useState([]);
  const [loadingCands, setLoadingCands] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [mq, setMq] = useState({ q: '', date_from: '', date_to: '', min_amount: '', max_amount: '', record_type: '', store_id: '' });
  const [mResults, setMResults] = useState(null);
  const [mLoading, setMLoading] = useState(false);

  const runManualSearch = async (override = {}) => {
    const params = { ...mq, ...override };
    Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null) delete params[k]; });
    setMLoading(true);
    try {
      const { data } = await apiClient.get('/conciliacion-manual/search-records', { params });
      setMResults(data.results || []);
    } catch { setMessage({ type: 'err', text: 'No se pudo buscar.' }); }
    setMLoading(false);
  };

  const startManual = (mov) => {
    const d = (mov.date || '').replace(/\//g, '-').slice(0, 10);
    const base = d ? new Date(d + 'T12:00:00') : new Date();
    const iso = (x) => x.toISOString().slice(0, 10);
    const from = iso(new Date(base.getTime() - 30 * 86400000)), to = iso(new Date(base.getTime() + 7 * 86400000));
    const q0 = { q: '', date_from: from, date_to: to, min_amount: '', max_amount: '', record_type: '', store_id: mov.store_id || '' };
    setMq(q0); setManualMode(true); setMResults(null);
    runManualSearch(q0);
  };

  const openSearch = async (mov) => {
    setSearchMov(mov); setCandidates([]); setLoadingCands(true); setManualMode(false); setMResults(null);
    try {
      const { data } = await apiClient.get(`/conciliacion-manual/candidates/${mov.id}`);
      setCandidates(data.candidates || []);
    } catch { setMessage({ type: 'err', text: 'No se pudo buscar el recibo.' }); }
    setLoadingCands(false);
  };

  const linkTo = async (mov, cand) => {
    setLinkingId(cand.id);
    try {
      const { data } = await apiClient.post(`/conciliacion-manual/link/${mov.id}`, {
        upload_id: selectedUpload, record_type: cand.type, record_id: cand.id, label: cand.title,
      });
      setManualStatus(prev => ({ ...prev, [mov.id]: { ...prev[mov.id], status: 'matched', linked_record: data.linked_record, notes: `Recibo: ${cand.title}` } }));
      setSearchMov(null);
      setMessage({ type: 'ok', text: '✓ Conciliado con su recibo' }); setTimeout(() => setMessage(null), 1500);
    } catch (e) { setMessage({ type: 'err', text: e.response?.data?.detail || 'No se pudo conciliar.' }); }
    setLinkingId(null);
  };

  const flagNoReceipt = async (mov) => {
    const notes = window.prompt('No hay recibo para esta salida. ¿Qué quieres preguntar? (se envía por Telegram)', '¿Qué fue este movimiento? No aparece pago ni gasto con comprobante.');
    if (notes === null) return;
    try {
      await apiClient.post(`/conciliacion-manual/flag/${mov.id}`, { upload_id: selectedUpload, notes });
      setManualStatus(prev => ({ ...prev, [mov.id]: { ...prev[mov.id], status: 'consulta', notes } }));
      setSearchMov(null);
      setMessage({ type: 'ok', text: '❓ Marcado sin recibo y preguntado por Telegram' }); setTimeout(() => setMessage(null), 2000);
    } catch (e) { setMessage({ type: 'err', text: e.response?.data?.detail || 'No se pudo marcar.' }); }
  };

  // Load uploads + stores
  useEffect(() => {
    apiClient.get('/bank-reconciliation/uploads', { params: { days: 120 } })
      .then(r => setUploads(r.data))
      .catch(() => {});
    apiClient.get('/stores')
      .then(r => {
        const map = {};
        r.data.forEach(s => { map[s.id] = s.name; });
        setStores(map);
      })
      .catch(() => {});
  }, []);

  const loadData = useCallback(async (uploadId) => {
    if (!uploadId) return;
    setLoading(true);
    try {
      // Read movements (read-only — never written back to bank-reconciliation)
      const [movRes, statusRes] = await Promise.all([
        apiClient.get('/bank-reconciliation/movements', { params: { upload_id: uploadId } }),
        apiClient.get('/conciliacion-manual/status', { params: { upload_id: uploadId } }),
      ]);
      const movs = movRes.data;
      const status = statusRes.data;

      // Auto-conciliar ingresos y comisiones que aún no tienen estado propio
      const toAutoMatch = movs
        .filter(m => isAutoMatch(m) && !status[m.id])
        .map(m => m.id);

      if (toAutoMatch.length > 0) {
        await apiClient.post('/conciliacion-manual/auto-match', {
          upload_id: uploadId,
          movement_ids: toAutoMatch,
        });
        toAutoMatch.forEach(id => {
          status[id] = { status: 'matched', notes: 'Auto-conciliado' };
        });
      }

      setMovements(movs);
      setManualStatus({ ...status });
    } catch {
      setMessage({ type: 'err', text: 'No se pudieron cargar los movimientos.' });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setMovements([]);
    setManualStatus({});
    loadData(selectedUpload);
  }, [selectedUpload, loadData]);

  // El estado de conciliación manual viene SOLO de manualStatus (colección propia)
  const getStatus = (mov) => manualStatus[mov.id]?.status || 'pending';

  const toggle = async (mov) => {
    const current = getStatus(mov);
    const newStatus = current === 'matched' ? 'pending' : 'matched';
    setTogglingId(mov.id);
    try {
      await apiClient.put(`/conciliacion-manual/toggle/${mov.id}`, {
        upload_id: selectedUpload,
        status: newStatus,
        notes: newStatus === 'matched' ? 'Conciliación manual' : '',
      });
      setManualStatus(prev => ({
        ...prev,
        [mov.id]: { ...prev[mov.id], status: newStatus },
      }));
      if (newStatus === 'matched') {
        setMessage({ type: 'ok', text: '✓ Marcado como conciliado' });
        setTimeout(() => setMessage(null), 1500);
      }
    } catch {
      setMessage({ type: 'err', text: 'Error al actualizar el estado.' });
    }
    setTogglingId(null);
  };

  const matched = movements.filter(m => getStatus(m) === 'matched').length;
  const total = movements.length;
  const progress = total > 0 ? Math.round((matched / total) * 100) : 0;

  const visible = movements.filter(m => {
    if (filter === 'pending') return getStatus(m) !== 'matched';
    if (filter === 'matched') return getStatus(m) === 'matched';
    return true;
  });

  const byDate = visible.reduce((acc, m) => {
    const d = m.date || 'Sin fecha';
    if (!acc[d]) acc[d] = [];
    acc[d].push(m);
    return acc;
  }, {});
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  // Group uploads by upload_date for the selector
  const uploadsByDate = uploads.reduce((acc, u) => {
    const key = formatUploadDate(u.upload_date);
    if (!acc[key]) acc[key] = [];
    acc[key].push(u);
    return acc;
  }, {});
  const sortedUploadDates = Object.keys(uploadsByDate).sort((a, b) => {
    const toISO = (label) => {
      const found = uploads.find(u => formatUploadDate(u.upload_date) === label);
      return found?.upload_date || '';
    };
    return toISO(b).localeCompare(toISO(a));
  });


  const renderCand = (c) => (

                <div key={c.type + c.id} className={`border rounded-lg p-3 ${c.score >= 5 ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.type === 'payment' ? 'bg-blue-100 text-blue-800' : c.type === 'transfer' ? 'bg-teal-100 text-teal-800' : 'bg-purple-100 text-purple-800'}`}>{c.type === 'payment' ? 'PAGO FACTURA' : c.type === 'transfer' ? 'TRASPASO' : 'GASTO'}</span>
                        <span className="font-medium text-gray-900 text-sm">{c.title}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {c.date} · {c.store_name || 'General'} · {c.method || '—'} · $ {formatNumber(c.amount_usd)} / Bs {formatNumber(c.amount_ves)}
                        {c.created_by_name ? ` · registró ${c.created_by_name}` : ''}
                      </p>
                      {c.references?.length > 0 && <p className="text-xs text-gray-400 font-mono">Ref: {c.references.join(' / ')}</p>}
                      <p className="text-xs mt-1">{c.reasons.map(r => <span key={r} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-800">{r}</span>)}</p>
                      {c.already_linked && <p className="text-[11px] text-orange-700 mt-0.5">⚠ Ya está conciliado con otra salida del {c.already_linked.date}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <div className="flex gap-1 flex-wrap justify-end">
                        {c.documents.length === 0
                          ? <span className="text-xs text-red-600 font-semibold">Sin PDF</span>
                          : c.documents.map((d, i) => (
                            <button key={d.file_id} onClick={() => c.type === 'payment' ? openPaymentDocumentFile(c.id, d.file_id) : c.type === 'transfer' ? openTransferDocumentFile(c.id, d.file_id) : openExpenseDocumentFile(c.id, d.file_id)}
                              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800" title={d.filename}>📄 PDF {c.documents.length > 1 ? i + 1 : ''}</button>
                          ))}
                      </div>
                      <button onClick={() => linkTo(searchMov, c)} disabled={linkingId === c.id}
                        className="text-sm px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50">
                        {linkingId === c.id ? '…' : '✓ Es este · conciliar'}
                      </button>
                    </div>
                  </div>
                </div>
              
  );

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Conciliación Manual</h1>
        <p className="text-gray-500 text-sm mt-1">
          Marca cada movimiento como conciliado mientras revisas tus papeles físicos.
          Este módulo es independiente — no afecta ni es afectado por el módulo de Conciliación.
        </p>
      </div>

      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
          message.type === 'ok'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right font-bold ml-4">×</button>
        </div>
      )}

      {/* Selector + progreso */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado de cuenta</label>
            <select
              value={selectedUpload}
              onChange={e => setSelectedUpload(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecciona un estado de cuenta…</option>
              {sortedUploadDates.map(dateLabel => (
                <optgroup key={dateLabel} label={`Subido: ${dateLabel}`}>
                  {uploadsByDate[dateLabel]
                    .sort((a, b) => toSortable(b.period_end).localeCompare(toSortable(a.period_end)))
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.bank_name}{u.account_name ? ` — ${u.account_name}` : ''} · {stores[u.store_id] || 'Sin tienda'} · {formatDate(u.period_start)} → {formatDate(u.period_end)}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>

          {total > 0 && (
            <div className="sm:text-right shrink-0">
              <p className="text-sm text-gray-500">Conciliados</p>
              <p className="text-2xl font-bold text-gray-900">
                {matched}<span className="text-gray-400 font-normal text-lg">/{total}</span>
              </p>
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progreso</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  progress === 100 ? 'bg-green-500' : progress > 50 ? 'bg-blue-500' : 'bg-yellow-400'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Filtros */}
      {total > 0 && (
        <div className="flex gap-2">
          {[
            { id: 'all', label: `Todos (${total})` },
            { id: 'pending', label: `Pendientes (${total - matched})` },
            { id: 'matched', label: `Conciliados (${matched})` },
            { id: 'consulta', label: `Sin recibo (${movements.filter(m => getStatus(m) === 'consulta').length})` },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                filter === f.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Lista */}
      {!selectedUpload ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-400">
          <p className="text-lg">Selecciona un estado de cuenta para comenzar</p>
        </div>
      ) : loading ? (
        <div className="text-center py-12 text-gray-400">Cargando movimientos…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
          No hay movimientos en este filtro.
        </div>
      ) : (
        <div className="space-y-4">
          {sortedDates.map(date => (
            <div key={date} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                <span className="font-semibold text-gray-700 text-sm">{formatDate(date)}</span>
                <span className="text-xs text-gray-400">
                  {byDate[date].filter(m => getStatus(m) === 'matched').length}/{byDate[date].length} conciliados
                </span>
              </div>

              <div className="divide-y divide-gray-100">
                {byDate[date].map(mov => {
                  const st = getStatus(mov);
                  const isMatched = st === 'matched';
                  const isConsulta = st === 'consulta';
                  const linked = manualStatus[mov.id]?.linked_record;
                  const toggling = togglingId === mov.id;
                  const cat = CATEGORY_LABELS[mov.category] || mov.category || '—';
                  const isNeg = mov.amount < 0;

                  return (
                    <div
                      key={mov.id}
                      className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                        isMatched ? 'bg-green-50' : isConsulta ? 'bg-orange-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-1.5 h-10 rounded-full shrink-0 ${
                        isMatched ? 'bg-green-400' : isConsulta ? 'bg-orange-400' : 'bg-yellow-300'
                      }`} />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {mov.description || '—'}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">
                            {cat}
                          </span>
                        </div>
                        {mov.reference && (
                          <p className="text-xs text-gray-400 mt-0.5 font-mono truncate">
                            Ref: {mov.reference}
                          </p>
                        )}
                        {linked && (
                          <p className="text-xs text-green-700 mt-0.5 truncate">📎 {linked.label}{linked.has_documents === false && <span className="text-orange-600"> · sin PDF</span>}</p>
                        )}
                        {isConsulta && (
                          <p className="text-xs text-orange-700 mt-0.5 truncate">❓ Sin recibo · {manualStatus[mov.id]?.notes}</p>
                        )}
                      </div>

                      <div className={`text-right shrink-0 ${isNeg ? 'text-red-600' : 'text-green-700'}`}>
                        <span className="font-semibold text-sm">
                          {isNeg ? '−' : '+'}{formatNumber(Math.abs(mov.amount))}
                        </span>
                        <p className="text-xs text-gray-400">{isNeg ? 'Egreso' : 'Ingreso'}</p>
                      </div>

                      {isNeg && !isMatched ? (
                        <button
                          onClick={() => openSearch(mov)}
                          className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                          title="Buscar el pago o gasto con su comprobante PDF"
                        >
                          🔎 Buscar recibo
                        </button>
                      ) : (
                        <button
                          onClick={() => toggle(mov)}
                          disabled={toggling}
                          className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                            toggling
                              ? 'bg-gray-200 text-gray-400 cursor-wait'
                              : isMatched
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                          }`}
                        >
                          {toggling ? '…' : isMatched ? '✓ Conciliado' : 'Conciliar'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: buscar recibo */}
      {searchMov && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSearchMov(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900">Buscar recibo de esta salida</h3>
                <p className="text-sm text-gray-600 mt-0.5">{searchMov.description}</p>
                <p className="text-xs text-gray-500 font-mono">{searchMov.date} · Bs {formatNumber(Math.abs(searchMov.amount))}{searchMov.reference ? ` · Ref ${searchMov.reference}` : ''}</p>
              </div>
              <button onClick={() => setSearchMov(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-3">
              {loadingCands ? (
                <p className="text-sm text-gray-400">Buscando pagos y gastos que coincidan…</p>
              ) : candidates.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-700 font-medium">La búsqueda automática no encontró ningún pago ni gasto que coincida por referencia, monto o fecha.</p>
                  <p className="text-sm text-gray-500 mt-1">Busca a mano entre todos los recibos antes de preguntar.</p>
                </div>
              ) : candidates.map(renderCand)}


              {/* Búsqueda manual entre todos los recibos */}
              {!manualMode ? (
                <div className="pt-2 border-t border-gray-100">
                  <button onClick={() => startManual(searchMov)} className="text-sm px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-800 font-semibold w-full">
                    🗂 No es ninguno · buscar entre todos los recibos
                  </button>
                </div>
              ) : (
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Buscar entre todos los pagos y gastos</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    <input value={mq.q} onChange={e => setMq(x => ({ ...x, q: e.target.value }))} onKeyDown={e => e.key === 'Enter' && runManualSearch()} placeholder="Proveedor, factura, concepto, referencia" className="form-input col-span-2 md:col-span-4 text-sm" autoFocus />
                    <input type="date" value={mq.date_from} onChange={e => setMq(x => ({ ...x, date_from: e.target.value }))} className="form-input text-sm" title="Desde" />
                    <input type="date" value={mq.date_to} onChange={e => setMq(x => ({ ...x, date_to: e.target.value }))} className="form-input text-sm" title="Hasta" />
                    <input type="number" value={mq.min_amount} onChange={e => setMq(x => ({ ...x, min_amount: e.target.value }))} placeholder="Bs mín" className="form-input text-sm" />
                    <input type="number" value={mq.max_amount} onChange={e => setMq(x => ({ ...x, max_amount: e.target.value }))} placeholder="Bs máx" className="form-input text-sm" />
                    <select value={mq.record_type} onChange={e => setMq(x => ({ ...x, record_type: e.target.value }))} className="form-select text-sm"><option value="">Pagos y gastos</option><option value="payment">Solo pagos de facturas</option><option value="expense">Solo gastos</option><option value="transfer">Solo traspasos</option></select>
                    <select value={mq.store_id} onChange={e => setMq(x => ({ ...x, store_id: e.target.value }))} className="form-select text-sm"><option value="">Todas las tiendas</option>{Object.entries(stores).map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select>
                    <button onClick={() => runManualSearch()} disabled={mLoading} className="btn-primary text-sm col-span-2">{mLoading ? 'Buscando…' : '🔎 Buscar'}</button>
                  </div>
                  {mResults === null ? null : mResults.length === 0 ? (
                    <p className="text-sm text-gray-500 py-3 text-center">Nada con esos filtros. Amplía las fechas o quita el texto.</p>
                  ) : (
                    <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                      <p className="text-xs text-gray-400">{mResults.length} resultado{mResults.length !== 1 ? 's' : ''}{mResults.length >= 60 ? ' (mostrando los 60 más recientes, afina la búsqueda)' : ''}</p>
                      {mResults.map(renderCand)}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                <button onClick={() => flagNoReceipt(searchMov)} className="text-sm px-3 py-1.5 rounded-lg bg-orange-100 hover:bg-orange-200 text-orange-800 font-semibold">❓ Sin recibo · preguntar</button>
                <button onClick={() => { toggle(searchMov); setSearchMov(null); }} className="text-xs text-gray-400 hover:text-gray-600">marcar conciliado sin recibo</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualReconciliation;
